import React, { useEffect, useState } from "react";
import { ProductOrderWrapper } from "../Styled";
import { CreateProductOrderTitleBar } from "../../../configs/productOrder";
import IconButton from "../../../shared/components/IconButton";
import BackIcon from "../../../shared/svg/BackIcon";
import { BackButtonRow, BackButtonStyle, ButtonRow } from "./Styled";
import SelectCustomer from "./SelectCustomers";
import { useLocation, useNavigate } from "react-router-dom";
import SingleProductOrder from "../single";
import { getInitialDeliveryAddress } from "../single/DeliveryAddress";
import useMergeState from "../../../shared/hooks/useMergeState";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import CancelModal from "../../../shared/components/CancelModal";
import { WarningDiscardCreateProductOrder, WarningUnsavedChanges } from "../../../configs/warningProductModal";
import { StyledButton, TitleText, TitleWrapper } from "../../../shared/styled/TitleBar";
import LoadingButton from "../../../shared/components/LoadingButton";
import { useContext } from "react";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { ROLE, SALE_ORDER_TYPE } from "../../../data/enums";
import ReserveOrderForm from "../../ReserveOrder/ReserveOrderForm";
import { getWorkshopForRole } from "../../ReserveOrder/utils";
import { useSearchParams } from "react-router-dom";
import OrderConflictModal, {
    OrderConflict,
} from "../../SaleOrder/create/OrderConflictModal";

const initialProductOrder = {
    due_date: new Date().toISOString().split("T")[0],
    po_number: "",
    delivery_address: "",
    is_urgent: false,
    products: [],
    materials: [],
    note: "",
    notes: "",
    order_type: SALE_ORDER_TYPE.SALE,
    workshop: null,
};

const CreateProductOrder = () => {
    const [searchParams] = useSearchParams();
    const isReserve = searchParams.get("type") === SALE_ORDER_TYPE.RESERVE;
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const roleWorkshop = getWorkshopForRole(role);
    const [productOrder, mergeProductOrder] = useMergeState(initialProductOrder);
    const [{ isCreating }, createProductOrder] = api.post("/product-flow/v2/orders/");
    const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
    const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [isModified, setIsModified] = useState(false);
    const [poNumberError, setPoNumberError] = useState("");
    const [orderConflict, setOrderConflict] = useState<OrderConflict | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { customer } = productOrder;
    const hasReserveDraft =
        isReserve &&
        Boolean(
            productOrder.workshop ||
                (productOrder.products || []).length ||
                productOrder.note,
        );

    useEffect(() => {
        if (!isReserve) return;
        mergeProductOrder({
            order_type: SALE_ORDER_TYPE.RESERVE,
            customer: null,
            materials: [],
            workshop: roleWorkshop || productOrder.workshop || null,
        });
        // Initialize the reserve mode once when the route or role changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReserve, roleWorkshop]);

    useEffect(() => {
        if (!isReserve && location.state?.customer && !customer) {
            mergeProductOrder({
                customer: location.state.customer,
                delivery_address: getInitialDeliveryAddress(location.state.customer),
            });
        }
    }, [isReserve, location.state, customer, mergeProductOrder]);

    // Mark as modified whenever productOrder changes after initial load
    useEffect(() => {
        if (customer || hasReserveDraft) {
            setIsModified(true);
        }
    }, [productOrder, customer, hasReserveDraft]);

    // Handle browser navigation/refresh with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const navigateWithCheck = (path: string) => {
        if (isModified && (customer || hasReserveDraft)) {
            setPendingNavigation(path);
            setIsUnsavedChangesOpen(true);
        } else {
            navigate(path);
        }
    };

    const handleUnsavedChangesConfirm = () => {
        setIsUnsavedChangesOpen(false);
        if (pendingNavigation) {
            onClickCreateProductOrder();
        }
    };

    const handleUnsavedChangesClose = () => {
        setIsUnsavedChangesOpen(false);
        if (pendingNavigation) {
            navigate(pendingNavigation);
        }
    };

    const onClickBack = () => {
        if (isReserve) {
            navigateWithCheck("/product-orders/");
            return;
        }
        if (customer) {
            if (isModified) {
                setPendingNavigation(location?.state?.customer ? `/customers/${location.state.customer.id}` : "/product-orders/");
                setIsUnsavedChangesOpen(true);
            } else {
                mergeProductOrder({ customer: null });
                if (location?.state?.customer) {
                    navigate(`/customers/${location.state.customer.id}`);
                } else {
                    navigate("/product-orders/");
                }
            }
        } else {
            navigate("/product-orders/");
        }
    };

    const onClickCancel = () => {
        navigateWithCheck("/product-orders/");
    };

    const onClickCreateProductOrder = (confirmSimilarOrder = false) => {
        // Validate required fields
        if (!productOrder.due_date) {
            error("Vui lòng chọn ngày đến hạn");
            return;
        }

        const deliveryAddress = String(productOrder.delivery_address || "").trim();
        if (!isReserve && !deliveryAddress) {
            error("Vui lòng nhập địa chỉ giao hàng");
            return;
        }

        const poNumber = String(productOrder.po_number || "").trim();

        const processedProducts = (productOrder.products || []).map((item: any) => ({
            product_id: item.id,
            ...(!isReserve && { price: item.product_price || item.price }),
            quantity: item.selectedQuantity,
            ...(!isReserve && {
                discount: item.discount || 0,
                markup: item.markup || 0,
            }),
        }));

        if (isReserve && !productOrder.workshop) {
            error("Vui lòng chọn xưởng sản xuất");
            return;
        }
        if (
            isReserve &&
            (processedProducts.length === 0 ||
                processedProducts.some((item: any) => Number(item.quantity) <= 0))
        ) {
            error("Vui lòng chọn sản phẩm và nhập số lượng sản xuất lớn hơn 0");
            return;
        }

        const processedMaterials = (productOrder.materials || []).map((item: any) => ({
            material_id: item.id,
            price: item.material_price || item.price,
            quantity: item.selectedQuantity,
            discount: item.discount || 0,
            markup: item.markup || 0,
        }));

        // const productPrice = sum((productOrder.products || []).map((item: any) => {
        //     const price = item.product_price || item.price || 0;
        //     return price * (item.selectedQuantity || 0);
        // }));
        //
        // const materialPrice = sum((productOrder.materials || []).map((item: any) => {
        //     const price = item.material_price || item.price || 0;
        //     return price * (item.selectedQuantity || 0);
        // }));

        const requestBody = isReserve
            ? {
                  order_type: SALE_ORDER_TYPE.RESERVE,
                  workshop: productOrder.workshop,
                  customer_id: null,
                  due_date: productOrder.due_date,
                  notes: productOrder.notes || productOrder.note || "",
                  items: processedProducts,
              }
            : {
                  order_type: SALE_ORDER_TYPE.SALE,
                  customer_id: productOrder.customer.id,
                  po_number: poNumber,
                  delivery_address: deliveryAddress,
                  due_date: productOrder.due_date,
                  is_urgent: Boolean(productOrder.is_urgent),
                  notes: productOrder.notes || productOrder.note || "",
                  confirm_similar_order: confirmSimilarOrder,
                  items: [...processedProducts, ...processedMaterials],
              };

        createProductOrder(requestBody)
            .then((resp: any) => {
                success(
                    "THÀNH CÔNG",
                    isReserve
                        ? "Tạo đơn dự phòng thành công"
                        : "Tạo đơn hàng sản phẩm thành công",
                );
                const saleOrderId = resp.id;
                setIsModified(false);
                setOrderConflict(null);
                navigate(`/product-orders/${saleOrderId}`, { replace: true });
            })
            .catch((err: any) => {
                if (err?.code === "DUPLICATE_PO") {
                    setPoNumberError(err.message || "Mã PO đã tồn tại.");
                    setOrderConflict(err as OrderConflict);
                    return;
                }
                if (err?.code === "SIMILAR_ORDER_WARNING") {
                    setOrderConflict(err as OrderConflict);
                    return;
                }
                const poFieldError = Array.isArray(err?.po_number)
                    ? err.po_number[0]
                    : err?.po_number;
                if (poFieldError) {
                    setPoNumberError(String(poFieldError));
                }
                const errorMessage =
                    poFieldError || err?.message || "Có lỗi xảy ra khi tạo đơn hàng";
                error(`Lỗi tạo đơn hàng sản phẩm: ${errorMessage}`);
            });
    };

    return (
        <ProductOrderWrapper>
            {isOpenCancelModal && (
                <CancelModal
                    open={isOpenCancelModal}
                    title={WarningDiscardCreateProductOrder.title}
                    contentText={WarningDiscardCreateProductOrder.context}
                    handleConfirm={() => onClickCancel()}
                    handleClose={() => setIsOpenCancelModal(false)}
                />
            )}
            {isUnsavedChangesOpen && (
                <CancelModal
                    open={isUnsavedChangesOpen}
                    title={WarningUnsavedChanges.title}
                    contentText={WarningUnsavedChanges.context}
                    closeButtonText={WarningUnsavedChanges.closeButtonText}
                    confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                    cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                    handleConfirm={handleUnsavedChangesConfirm}
                    handleClose={handleUnsavedChangesClose}
                    handleCancel={() => setIsUnsavedChangesOpen(false)}
                    showCancelButton={true}
                />
            )}
            <OrderConflictModal
                conflict={orderConflict}
                isLoading={isCreating}
                onClose={() => setOrderConflict(null)}
                onConfirmSimilar={() => {
                    setOrderConflict(null);
                    onClickCreateProductOrder(true);
                }}
            />
            <BackButtonRow>
                <IconButton style={BackButtonStyle} onClick={onClickBack}>
                    <BackIcon />
                </IconButton>
                Trở về
            </BackButtonRow>

            <TitleWrapper
                style={
                    isReserve
                        ? { flexWrap: "wrap", gap: "12px" }
                        : undefined
                }
            >
                <TitleText>
                    {isReserve
                        ? "Tạo đơn dự phòng"
                        : CreateProductOrderTitleBar.title}
                </TitleText>
                {(customer || isReserve) && (
                    <ButtonRow
                        style={
                            isReserve
                                ? { flexWrap: "wrap" }
                                : undefined
                        }
                    >
                        <StyledButton variant="text" onClick={() => setIsOpenCancelModal(true)} disabled={isCreating}>
                            Huỷ bỏ
                        </StyledButton>
                        <LoadingButton variant="contained" onClick={() => onClickCreateProductOrder()} isLoading={isCreating}
                                       text={isReserve ? "Tạo đơn dự phòng" : "Tạo đơn hàng"} />
                    </ButtonRow>
                )}
            </TitleWrapper>

            {!isReserve && !customer && !location?.state?.customer && (
                <SelectCustomer setSelectedCustomer={(customer: any) => mergeProductOrder({
                    customer,
                    delivery_address: getInitialDeliveryAddress(customer),
                })} />
            )}

            {isReserve && (
                <ReserveOrderForm
                    productOrder={productOrder as any}
                    mergeProductOrder={(updates: any) => {
                        mergeProductOrder(updates);
                        setIsModified(true);
                    }}
                    canEditWorkshop={
                        !roleWorkshop &&
                        [ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(role)
                    }
                />
            )}

            {!isReserve && customer && <SingleProductOrder
                productOrder={productOrder}
                mergeProductOrder={(updates: any) => {
                    mergeProductOrder(updates);
                    if (updates.po_number !== undefined) {
                        setPoNumberError("");
                    }
                    setIsModified(true);
                }}
                poNumberError={poNumberError}
            />}
        </ProductOrderWrapper>
    );
};

export default CreateProductOrder;
